/**
 * surebetUtils.js - Comprehensive utility functions for surebet data processing
 *
 * Features:
 * - Margin calculations and validation
 * - Coefficient processing and formatting
 * - Data sorting and filtering
 * - Date/time formatting
 * - Statistics calculations
 * - Data transformation utilities
 */

import { MARGIN_TYPES, BOOKMAKERS, VALIDATION_RULES } from '../constants/surebetTypes';
// Import DateUtils and DATE_FORMATS from modules/utils (Component 12)
import DateUtils, { DATE_FORMATS } from '../../../modules/utils/DateUtils';

/**
 * ====================
 * MARGIN UTILITIES
 * ====================
 */

/**
 * Calculate margin from coefficients
 * Formula: margin = (1/coef1 + 1/coef2 + ... + 1/coefN) - 1
 * Positive margin = profitable arbitrage opportunity
 *
 * @param {Array<number>} coefficients - Array of coefficient values
 * @returns {number} Calculated margin as percentage
 */
export const calculateMargin = (coefficients) => {
  if (!Array.isArray(coefficients) || coefficients.length === 0) {
    return 0;
  }

  try {
    const sum = coefficients.reduce((acc, coef) => {
      if (!coef || coef <= 0) return acc;
      return acc + (1 / coef);
    }, 0);

    const margin = (1 / sum - 1) * 100;
    return parseFloat(margin.toFixed(2));
  } catch (error) {
    console.error('Error calculating margin:', error);
    return 0;
  }
};

/**
 * Validate margin value
 *
 * @param {number} margin - Margin value to validate
 * @returns {boolean} True if margin is valid
 */
export const isValidMargin = (margin) => {
  if (typeof margin !== 'number' || isNaN(margin)) {
    return false;
  }

  return margin >= VALIDATION_RULES.MIN_MARGIN && margin <= VALIDATION_RULES.MAX_MARGIN;
};

/**
 * Check if margin is profitable
 *
 * @param {number} margin - Margin value
 * @returns {boolean} True if margin is profitable (> 0)
 */
export const isProfitableMargin = (margin) => {
  return isValidMargin(margin) && margin > 0;
};

/**
 * Get highest margin from a surebet item
 *
 * @param {Object} surebet - Surebet item with margins object
 * @returns {number} Highest margin value
 */
export const getMaxMargin = (surebet) => {
  if (!surebet || !surebet.margins) {
    return 0;
  }

  try {
    const margins = Object.values(surebet.margins);
    if (margins.length === 0) return 0;

    const maxMargin = Math.max(...margins.map(m => m.value || 0));
    return parseFloat(maxMargin.toFixed(2));
  } catch (error) {
    console.error('Error getting max margin:', error);
    return 0;
  }
};

/**
 * Get margin type with highest value
 *
 * @param {Object} surebet - Surebet item with margins object
 * @returns {Object|null} { key, value, data } or null
 */
export const getMaxMarginType = (surebet) => {
  if (!surebet || !surebet.margins) {
    return null;
  }

  try {
    let maxKey = null;
    let maxValue = -Infinity;
    let maxData = null;

    for (const [key, data] of Object.entries(surebet.margins)) {
      if (data.value > maxValue) {
        maxValue = data.value;
        maxKey = key;
        maxData = data;
      }
    }

    return maxKey ? { key: maxKey, value: maxValue, data: maxData } : null;
  } catch (error) {
    console.error('Error getting max margin type:', error);
    return null;
  }
};

/**
 * Format margin as percentage string
 *
 * @param {number} margin - Margin value
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted margin (e.g., "2.50%")
 */
export const formatMarginPercentage = (margin, decimals = 2) => {
  if (typeof margin !== 'number' || isNaN(margin)) {
    return '0.00%';
  }

  return `${margin.toFixed(decimals)}%`;
};

/**
 * ====================
 * COEFFICIENT UTILITIES
 * ====================
 */

/**
 * Validate coefficient value
 *
 * @param {number} coefficient - Coefficient value to validate
 * @returns {boolean} True if coefficient is valid
 */
export const isValidCoefficient = (coefficient) => {
  if (typeof coefficient !== 'number' || isNaN(coefficient)) {
    return false;
  }

  return coefficient >= VALIDATION_RULES.MIN_COEFFICIENT &&
         coefficient <= VALIDATION_RULES.MAX_COEFFICIENT;
};

/**
 * Format coefficient for display
 *
 * @param {number} coefficient - Coefficient value
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted coefficient (e.g., "1.85")
 */
export const formatCoefficient = (coefficient, decimals = 2) => {
  if (typeof coefficient !== 'number' || isNaN(coefficient)) {
    return '0.00';
  }

  return coefficient.toFixed(decimals);
};

/**
 * Get bookmaker name from ID
 *
 * @param {string} bookmakerId - Bookmaker identifier
 * @returns {string} Bookmaker display name
 */
export const getBookmakerName = (bookmakerId) => {
  const bookmaker = BOOKMAKERS[bookmakerId];
  return bookmaker ? bookmaker.name : bookmakerId;
};

/**
 * Get best coefficient from a list
 *
 * @param {Array<Object>} coefficients - Array of coefficient objects
 * @returns {Object|null} Coefficient object with highest value
 */
export const getBestCoefficient = (coefficients) => {
  if (!Array.isArray(coefficients) || coefficients.length === 0) {
    return null;
  }

  try {
    return coefficients.reduce((best, current) => {
      if (!best || current.value > best.value) {
        return current;
      }
      return best;
    }, null);
  } catch (error) {
    console.error('Error getting best coefficient:', error);
    return null;
  }
};

/**
 * Group coefficients by type
 *
 * @param {Array<Object>} coefficients - Array of coefficient objects
 * @returns {Object} Coefficients grouped by type
 */
export const groupCoefficientsByType = (coefficients) => {
  if (!Array.isArray(coefficients)) {
    return {};
  }

  try {
    return coefficients.reduce((acc, coef) => {
      const type = coef.type;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(coef);
      return acc;
    }, {});
  } catch (error) {
    console.error('Error grouping coefficients:', error);
    return {};
  }
};

/**
 * ====================
 * MARGIN TYPE UTILITIES
 * ====================
 */

/**
 * Get localized margin type name
 *
 * @param {string} marginKey - Margin type key (e.g., "margin1X2")
 * @param {string} language - Language code ('bg' or 'en')
 * @returns {string} Localized margin type name
 */
export const getMarginTypeName = (marginKey, language = 'bg') => {
  const marginType = MARGIN_TYPES[marginKey];
  if (!marginType) return marginKey;

  return marginType.description?.[language] || marginKey;
};

/**
 * Get localized outcome name
 *
 * @param {string} marginKey - Margin type key
 * @param {string} outcomeKey - Outcome key (e.g., "Coef1")
 * @param {string} language - Language code ('bg' or 'en')
 * @returns {string} Localized outcome name
 */
export const getOutcomeName = (marginKey, outcomeKey, language = 'bg') => {
  const marginType = MARGIN_TYPES[marginKey];
  if (!marginType) return outcomeKey;

  return marginType.outcomes?.[outcomeKey]?.[language] || outcomeKey;
};

/**
 * Get all available margin types from a surebet
 *
 * @param {Object} surebet - Surebet item
 * @returns {Array<string>} Array of margin type keys
 */
export const getAvailableMarginTypes = (surebet) => {
  if (!surebet || !surebet.margins) {
    return [];
  }

  return Object.keys(surebet.margins).filter(key => {
    const marginData = surebet.margins[key];
    return marginData && marginData.coefficients && marginData.coefficients.length > 0;
  });
};

/**
 * ====================
 * SORTING UTILITIES
 * ====================
 */

/**
 * Sort surebets by margin (highest first)
 *
 * @param {Array<Object>} surebets - Array of surebet items
 * @returns {Array<Object>} Sorted array
 */
export const sortByMargin = (surebets) => {
  if (!Array.isArray(surebets)) return [];

  return [...surebets].sort((a, b) => {
    const marginA = getMaxMargin(a);
    const marginB = getMaxMargin(b);
    return marginB - marginA;
  });
};

/**
 * Sort surebets by date/time (newest first)
 *
 * @param {Array<Object>} surebets - Array of surebet items
 * @returns {Array<Object>} Sorted array
 */
export const sortByDate = (surebets) => {
  if (!Array.isArray(surebets)) return [];

  return [...surebets].sort((a, b) => {
    const dateA = new Date(`${a.event?.date} ${a.event?.time}`);
    const dateB = new Date(`${b.event?.date} ${b.event?.time}`);
    return dateB - dateA;
  });
};

/**
 * Sort surebets by sport
 *
 * @param {Array<Object>} surebets - Array of surebet items
 * @returns {Array<Object>} Sorted array
 */
export const sortBySport = (surebets) => {
  if (!Array.isArray(surebets)) return [];

  return [...surebets].sort((a, b) => {
    const sportA = a.event?.sport || '';
    const sportB = b.event?.sport || '';
    return sportA.localeCompare(sportB);
  });
};

/**
 * Sort surebets by championship
 *
 * @param {Array<Object>} surebets - Array of surebet items
 * @returns {Array<Object>} Sorted array
 */
export const sortByChampionship = (surebets) => {
  if (!Array.isArray(surebets)) return [];

  return [...surebets].sort((a, b) => {
    const champA = a.event?.championship || '';
    const champB = b.event?.championship || '';
    return champA.localeCompare(champB);
  });
};

/**
 * Generic sort function
 *
 * @param {Array<Object>} surebets - Array of surebet items
 * @param {string} sortBy - Sort field ('margin', 'date', 'sport', 'championship')
 * @param {string} order - Sort order ('asc' or 'desc')
 * @returns {Array<Object>} Sorted array
 */
export const sortSurebets = (surebets, sortBy = 'margin', order = 'desc') => {
  if (!Array.isArray(surebets)) return [];

  let sorted = [...surebets];

  switch (sortBy) {
    case 'margin':
      sorted = sortByMargin(sorted);
      break;
    case 'date':
      sorted = sortByDate(sorted);
      break;
    case 'sport':
      sorted = sortBySport(sorted);
      break;
    case 'championship':
      sorted = sortByChampionship(sorted);
      break;
    default:
      sorted = sortByMargin(sorted);
  }

  // Reverse if ascending order
  if (order === 'asc') {
    sorted.reverse();
  }

  return sorted;
};

/**
 * ====================
 * FILTERING UTILITIES
 * ====================
 */

/**
 * Filter surebets by bookmakers
 *
 * @param {Array<Object>} surebets - Array of surebet items
 * @param {Array<string>} bookmakers - Array of bookmaker IDs
 * @returns {Array<Object>} Filtered array
 */
export const filterByBookmakers = (surebets, bookmakers) => {
  if (!Array.isArray(surebets) || !Array.isArray(bookmakers) || bookmakers.length === 0) {
    return surebets;
  }

  return surebets.filter(surebet => {
    // Check if any margin contains coefficients from the specified bookmakers
    for (const marginData of Object.values(surebet.margins || {})) {
      const hasBookmaker = marginData.coefficients?.some(coef =>
        bookmakers.includes(coef.bookmaker)
      );
      if (hasBookmaker) return true;
    }
    return false;
  });
};

/**
 * Filter surebets by sport
 *
 * @param {Array<Object>} surebets - Array of surebet items
 * @param {string} sport - Sport filter
 * @returns {Array<Object>} Filtered array
 */
export const filterBySport = (surebets, sport) => {
  if (!Array.isArray(surebets) || !sport) {
    return surebets;
  }

  return surebets.filter(surebet =>
    surebet.event?.sport?.toLowerCase() === sport.toLowerCase()
  );
};

/**
 * Filter surebets by championship
 *
 * @param {Array<Object>} surebets - Array of surebet items
 * @param {string} championship - Championship filter
 * @returns {Array<Object>} Filtered array
 */
export const filterByChampionship = (surebets, championship) => {
  if (!Array.isArray(surebets) || !championship) {
    return surebets;
  }

  return surebets.filter(surebet =>
    surebet.event?.championship?.toLowerCase().includes(championship.toLowerCase())
  );
};

/**
 * Filter surebets by minimum margin
 *
 * @param {Array<Object>} surebets - Array of surebet items
 * @param {number} minMargin - Minimum margin threshold
 * @returns {Array<Object>} Filtered array
 */
export const filterByMinMargin = (surebets, minMargin) => {
  if (!Array.isArray(surebets) || typeof minMargin !== 'number') {
    return surebets;
  }

  return surebets.filter(surebet => getMaxMargin(surebet) >= minMargin);
};

/**
 * Filter surebets by profitable flag
 *
 * @param {Array<Object>} surebets - Array of surebet items
 * @param {boolean} profitableOnly - Filter for profitable only
 * @returns {Array<Object>} Filtered array
 */
export const filterByProfitable = (surebets, profitableOnly = true) => {
  if (!Array.isArray(surebets) || !profitableOnly) {
    return surebets;
  }

  return surebets.filter(surebet => surebet.marginFlag === true);
};

/**
 * ====================
 * DATE/TIME UTILITIES
 * ====================
 */

/**
 * Format date for display
 * Uses DateUtils from modules/utils (Component 12)
 *
 * @param {string} dateString - Date string (YYYY-MM-DD)
 * @param {string} language - Language code ('bg' or 'en')
 * @returns {string} Formatted date
 */
export const formatDate = (dateString, language = 'bg') => {
  if (!dateString) return '';
  return DateUtils.formatDate(dateString, DATE_FORMATS.FULL_DATE_BG, language);
};

/**
 * Format time for display
 * Uses DateUtils from modules/utils (Component 12)
 *
 * @param {string} timeString - Time string (HH:MM)
 * @returns {string} Formatted time
 */
export const formatTime = (timeString) => {
  if (!timeString) return '';
  // Parse time string to date object and format
  try {
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10), parseInt(minutes, 10));
    return DateUtils.formatTime(date);
  } catch (error) {
    return timeString;
  }
};

/**
 * Format event date/time for display
 *
 * @param {Object} event - Event object with date and time
 * @param {string} language - Language code ('bg' or 'en')
 * @returns {string} Formatted date/time string
 */
export const formatEventDateTime = (event, language = 'bg') => {
  if (!event) return '';

  const date = formatDate(event.date, language);
  const time = formatTime(event.time);

  return `${date} ${time}`;
};

/**
 * Check if event is today
 * Uses DateUtils from modules/utils (Component 12)
 *
 * @param {string} dateString - Date string (YYYY-MM-DD)
 * @returns {boolean} True if event is today
 */
export const isToday = (dateString) => {
  if (!dateString) return false;
  return DateUtils.isToday(dateString);
};

/**
 * Get time until event
 *
 * @param {string} dateString - Date string (YYYY-MM-DD)
 * @param {string} timeString - Time string (HH:MM)
 * @returns {Object} { hours, minutes, isPast }
 */
export const getTimeUntilEvent = (dateString, timeString) => {
  if (!dateString || !timeString) {
    return { hours: 0, minutes: 0, isPast: false };
  }

  try {
    const eventDateTime = new Date(`${dateString} ${timeString}`);
    const now = new Date();
    const diff = eventDateTime - now;

    if (diff < 0) {
      return { hours: 0, minutes: 0, isPast: true };
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return { hours, minutes, isPast: false };
  } catch (error) {
    console.error('Error calculating time until event:', error);
    return { hours: 0, minutes: 0, isPast: false };
  }
};

/**
 * ====================
 * STATISTICS UTILITIES
 * ====================
 */

/**
 * Calculate statistics from surebets array
 *
 * @param {Array<Object>} surebets - Array of surebet items
 * @returns {Object} Statistics object
 */
export const calculateStatistics = (surebets) => {
  if (!Array.isArray(surebets) || surebets.length === 0) {
    return {
      total: 0,
      profitable: 0,
      averageMargin: 0,
      maxMargin: 0,
      minMargin: 0,
      totalMarkets: 0
    };
  }

  try {
    const profitable = surebets.filter(s => s.marginFlag === true).length;
    const margins = surebets.map(s => getMaxMargin(s)).filter(m => m > 0);

    const averageMargin = margins.length > 0
      ? margins.reduce((sum, m) => sum + m, 0) / margins.length
      : 0;

    const maxMargin = margins.length > 0 ? Math.max(...margins) : 0;
    const minMargin = margins.length > 0 ? Math.min(...margins) : 0;

    const totalMarkets = surebets.reduce((sum, surebet) => {
      return sum + Object.keys(surebet.margins || {}).length;
    }, 0);

    return {
      total: surebets.length,
      profitable,
      averageMargin: parseFloat(averageMargin.toFixed(2)),
      maxMargin: parseFloat(maxMargin.toFixed(2)),
      minMargin: parseFloat(minMargin.toFixed(2)),
      totalMarkets
    };
  } catch (error) {
    console.error('Error calculating statistics:', error);
    return {
      total: surebets.length,
      profitable: 0,
      averageMargin: 0,
      maxMargin: 0,
      minMargin: 0,
      totalMarkets: 0
    };
  }
};

/**
 * Group surebets by sport
 *
 * @param {Array<Object>} surebets - Array of surebet items
 * @returns {Object} Surebets grouped by sport
 */
export const groupBySport = (surebets) => {
  if (!Array.isArray(surebets)) return {};

  return surebets.reduce((acc, surebet) => {
    const sport = surebet.event?.sport || 'Unknown';
    if (!acc[sport]) {
      acc[sport] = [];
    }
    acc[sport].push(surebet);
    return acc;
  }, {});
};

/**
 * Group surebets by championship
 *
 * @param {Array<Object>} surebets - Array of surebet items
 * @returns {Object} Surebets grouped by championship
 */
export const groupByChampionship = (surebets) => {
  if (!Array.isArray(surebets)) return {};

  return surebets.reduce((acc, surebet) => {
    const championship = surebet.event?.championship || 'Unknown';
    if (!acc[championship]) {
      acc[championship] = [];
    }
    acc[championship].push(surebet);
    return acc;
  }, {});
};

/**
 * ====================
 * VALIDATION UTILITIES
 * ====================
 */

/**
 * Validate surebet data structure
 *
 * @param {Object} surebet - Surebet item to validate
 * @returns {boolean} True if valid
 */
export const isValidSurebet = (surebet) => {
  if (!surebet || typeof surebet !== 'object') {
    return false;
  }

  // Check required fields
  if (!surebet.id || !surebet.event) {
    return false;
  }

  // Check event fields
  const requiredEventFields = VALIDATION_RULES.REQUIRED_EVENT_FIELDS;
  for (const field of requiredEventFields) {
    if (!surebet.event.hasOwnProperty(field)) {
      return false;
    }
  }

  // Check margins
  if (!surebet.margins || typeof surebet.margins !== 'object') {
    return false;
  }

  return true;
};

/**
 * Sanitize surebet data
 *
 * @param {Object} surebet - Surebet item to sanitize
 * @returns {Object} Sanitized surebet
 */
export const sanitizeSurebet = (surebet) => {
  if (!surebet) return null;

  try {
    return {
      id: surebet.id || 0,
      event: {
        id: surebet.event?.id || 0,
        date: surebet.event?.date || '',
        time: surebet.event?.time || '',
        teamOne: surebet.event?.teamOne || '',
        teamTwo: surebet.event?.teamTwo || '',
        championship: surebet.event?.championship || '',
        sport: surebet.event?.sport || ''
      },
      marginFlag: surebet.marginFlag || false,
      margins: surebet.margins || {}
    };
  } catch (error) {
    console.error('Error sanitizing surebet:', error);
    return null;
  }
};

/**
 * ====================
 * EXPORT ALL UTILITIES
 * ====================
 */

export default {
  // Margin utilities
  calculateMargin,
  isValidMargin,
  isProfitableMargin,
  getMaxMargin,
  getMaxMarginType,
  formatMarginPercentage,

  // Coefficient utilities
  isValidCoefficient,
  formatCoefficient,
  getBookmakerName,
  getBestCoefficient,
  groupCoefficientsByType,

  // Margin type utilities
  getMarginTypeName,
  getOutcomeName,
  getAvailableMarginTypes,

  // Sorting utilities
  sortByMargin,
  sortByDate,
  sortBySport,
  sortByChampionship,
  sortSurebets,

  // Filtering utilities
  filterByBookmakers,
  filterBySport,
  filterByChampionship,
  filterByMinMargin,
  filterByProfitable,

  // Date/time utilities
  formatDate,
  formatTime,
  formatEventDateTime,
  isToday,
  getTimeUntilEvent,

  // Statistics utilities
  calculateStatistics,
  groupBySport,
  groupByChampionship,

  // Validation utilities
  isValidSurebet,
  sanitizeSurebet
};

